import { FC } from 'react'
import InlineSvg from 'react-inlinesvg'
import { observer } from 'mobx-react-lite'
import { PrismicRichText } from '@prismicio/react'
import { LinkField } from '@prismicio/client'
import tw from 'twin.macro'
import CircleBar from '../CircleBar'
import { Body, Grid, LargeHeader, SmallBody, SubHeader } from '../UI'
import PrismicLinkInternalOrExternal from '../PrismicLinkInternalOrExternal'
import { RichTextFields } from '../../types/prismic'

import ctaArrowBlue from '../../images/icons/cta-arrow-blue.svg'
import longPinkArrow from '../../images/icons/long-pink-arrow.svg'
import shortPinkArrow from '../../images/icons/short-pink-arrow.svg'

type Props = {
  header: string
  subhead: RichTextFields
  cta_text?: string
  cta_link?: LinkField
  percentage1: number
  copy1: string
  percentage2: number
  percentage_subtext2: string
  copy2: string
  percentage3: number
  percentage_subtext3: string
  copy3: string
  citation: string
  pagePath: string
  pageTitle: string
}

const KnowYourRisk: FC<Props> = ({
  header,
  subhead,
  cta_text,
  cta_link,
  percentage1,
  copy1,
  percentage2,
  percentage_subtext2,
  copy2,
  percentage3,
  percentage_subtext3,
  copy3,
  citation,
  pagePath,
  pageTitle,
}) => {
  return (
    <section tw="bg-gb-cream pt-[80px] pb-8 lg:(pt-[80px] pb-10) text-gb-blue font-domaine">
      <Grid tw="mb-[40px] lg:mb-[74px]">
        {/* Headline content */}
        <div tw="col-span-full lg:(col-span-8)">
          <h2 css={[Body, tw`mb-[10px] lg:mb-[25px]`]}>{header}</h2>
          <h3 css={[LargeHeader, tw`mb-[18px]`]}>
            <PrismicRichText
              field={subhead.richText}
              components={{
                strong: ({ children }) => (
                  <strong tw="uppercase font-object line-height[1] text-gb-magenta">
                    {children}
                  </strong>
                ),
              }}
            />
          </h3>
          {!!cta_text && !!cta_link && (
            <PrismicLinkInternalOrExternal
              id="cta-learn-your-risk-2"
              field={cta_link}
              trackEventParams={{
                pageTitle: pageTitle,
                path: pagePath,
                target: 'url' in cta_link ? cta_link.url : '',
                copy: cta_text,
              }}
              cssStyle={tw`uppercase pt-2 pb-1 font-object text-[13px] line-height[13px] letter-spacing[1px] border-b border-gb-blue lg:(transition-colors hover:bg-gb-cream-dark)`}
            >
              {cta_text}
              <InlineSvg
                src={ctaArrowBlue}
                tw="w-6 ml-[5px] mt-[-2px] inline text-white"
              />
            </PrismicLinkInternalOrExternal>
          )}
        </div>
      </Grid>
      <Grid>
        {/* Left */}
        <div tw="col-span-4 mb-3 lg:(col-span-5 mb-0)">
          <div css={[tw`line-height[1] lg:(flex items-center justify-start)`]}>
            <span tw="text-gb-magenta font-object text-[56px] lg:text-[80px]">
              {percentage1}
              <span tw="text-gb-magenta font-object text-[28px] lg:text-[40px]">
                %
              </span>
            </span>
            <img src={longPinkArrow} tw="hidden lg:(inline ml-6)" alt="" />
          </div>
          <div css={[SubHeader, tw`block lg:pr-24`]}>{copy1}</div>
          <img
            src={shortPinkArrow}
            tw="h-[50px] ml-[25%] mt-3 lg:hidden"
            alt=""
          />
        </div>
        {/* Right */}
        <div tw="col-span-full lg:col-span-7">
          <div tw="relative flex justify-between">
            {/* First stat */}
            <div tw="flex flex-col justify-between">
              <div
                css={[
                  SubHeader,
                  tw`text-center mb-[21px] text-[16px] line-height[19px] px-2`,
                ]}
              >
                {copy2}
              </div>
              <div tw="w-full mx-auto relative flex items-center justify-center lg:w-8/12">
                <div tw="w-full">
                  <CircleBar percentage={percentage2} />
                </div>
                <p tw="w-32 absolute text-center">
                  <span tw="text-gb-magenta font-object text-[36px] lg:text-[56px] line-height[1]">
                    {percentage2}
                    <span tw="text-gb-magenta font-object text-[18px] lg:text-[28px] line-height[1]">
                      %
                    </span>
                  </span>
                  <span
                    css={[Body, tw`block text-gb-magenta whitespace-pre-line`]}
                  >
                    {percentage_subtext2}
                  </span>
                </p>
              </div>
            </div>
            {/* Divider */}
            <div css={[SmallBody, tw`text-center`]}>
              <div tw="w-[1px] bg-gb-blue h-[47%] mx-auto mb-1" />
              vs
              <div tw="w-[1px] bg-gb-blue h-[47%] mx-auto mt-1" />
            </div>
            {/* Second stat */}
            <div tw="flex flex-col justify-between">
              <div
                css={[
                  SubHeader,
                  tw`text-center mb-[21px] text-[16px] line-height[19px] px-2`,
                ]}
              >
                {copy3}
              </div>
              <div tw="w-full mx-auto relative flex items-center justify-center lg:w-8/12">
                <div tw="w-full">
                  <CircleBar percentage={percentage3} />
                </div>
                <p tw="w-32 absolute text-center">
                  <span tw="text-gb-magenta font-object text-[36px] lg:text-[56px] line-height[1]">
                    {percentage3}
                    <span tw="text-gb-magenta font-object text-[18px] lg:text-[28px] line-height[1]">
                      %
                    </span>
                  </span>
                  <span
                    css={[Body, tw`block text-gb-magenta whitespace-pre-line`]}
                  >
                    {percentage_subtext3}
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>
        {/* Citation */}
        <div css={[SmallBody, tw`col-span-full mt-7`]}>{citation}</div>
      </Grid>
    </section>
  )
}

export default observer(KnowYourRisk)
